import { useAuth } from '../contexts/AuthContext.tsx';
import { Layout } from '../components/Layout.tsx';
import { UpdatePassword } from '../components/UpdatePassword.tsx';
import { useEffect, useState } from 'react';
import { systemSettingsAPI } from '../services/api.ts';
import toast from 'react-hot-toast';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceUntil, setMaintenanceUntil] = useState('');
  const [exemptEmails, setExemptEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<'security' | 'system'>('security');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchSettings();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const response = await systemSettingsAPI.getSettings();
      if (response.success) {
        setMaintenanceMode(response.data.maintenanceMode);
        setMaintenanceMessage(response.data.maintenanceMessage);
        setExemptEmails(response.data.exemptEmails || []);
        if (response.data.maintenanceUntil) {
          const date = new Date(response.data.maintenanceUntil);
          const formatted = date.toISOString().slice(0, 16);
          setMaintenanceUntil(formatted);
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleUpdateSettings = async (updates: any) => {
    setLoading(true);
    try {
      const response = await systemSettingsAPI.updateSettings(updates);
      if (response.success) {
        setMaintenanceMode(response.data.maintenanceMode);
        setMaintenanceMessage(response.data.maintenanceMessage);
        setExemptEmails(response.data.exemptEmails || []);
        if (response.data.maintenanceUntil) {
          const date = new Date(response.data.maintenanceUntil);
          const formatted = date.toISOString().slice(0, 16);
          setMaintenanceUntil(formatted);
        }
        toast.success(response.message || 'Settings updated');
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (newEmail && !exemptEmails.includes(newEmail)) {
      const updatedEmails = [...exemptEmails, newEmail];
      handleUpdateSettings({ exemptEmails: updatedEmails });
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    const updatedEmails = exemptEmails.filter((e) => e !== email);
    handleUpdateSettings({ exemptEmails: updatedEmails });
  };

  const tabs = [
    {
      id: 'security',
      name: 'Security',
      icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    },
    ...(user?.role === 'super_admin'
      ? [
          {
            id: 'system',
            name: 'System',
            icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
          },
        ]
      : []),
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your account security and system preferences.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200
                  ${
                    activeTab === tab.id
                      ? 'border-primary text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                <svg
                  className={`
                    -ml-0.5 mr-2 h-5 w-5
                    ${activeTab === tab.id ? 'text-primary dark:text-primary-400' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'}
                  `}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={tab.icon}
                  />
                </svg>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'security' && (
            <div className="animate-fadeIn">
              <UpdatePassword />
            </div>
          )}

          {activeTab === 'system' && user?.role === 'super_admin' && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 space-y-6 transition-colors border-2 border-primary/20">
                <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-4">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                      Maintenance Mode
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, non-exempt users will be blocked from
                      accessing the system.
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      handleUpdateSettings({
                        maintenanceMode: !maintenanceMode,
                      })
                    }
                    disabled={loading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                      maintenanceMode
                        ? 'bg-primary'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`${maintenanceMode ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maintenance Message
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={maintenanceMessage}
                        onChange={(e) => setMaintenanceMessage(e.target.value)}
                        className="form-input dark:text-white"
                        placeholder="Enter maintenance message..."
                      />
                      <button
                        onClick={() =>
                          handleUpdateSettings({ maintenanceMessage })
                        }
                        disabled={loading}
                        className="btn-primary whitespace-nowrap"
                      >
                        Update Message
                      </button>
                    </div>
                  </div>

                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maintenance Timer
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="datetime-local"
                        value={maintenanceUntil}
                        onChange={(e) => setMaintenanceUntil(e.target.value)}
                        className="form-input dark:text-white"
                      />
                      <button
                        onClick={() =>
                          handleUpdateSettings({ maintenanceUntil })
                        }
                        disabled={loading}
                        className="btn-primary whitespace-nowrap"
                      >
                        Update Timer
                      </button>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {[
                        { label: '30m', minutes: 30 },
                        { label: '1h', minutes: 60 },
                        { label: '3h', minutes: 180 },
                        { label: '6h', minutes: 360 },
                        { label: '12h', minutes: 720 },
                        { label: '1d', minutes: 1440 },
                      ].map((option) => (
                        <button
                          key={option.label}
                          onClick={() => {
                            const date = new Date(
                              Date.now() + option.minutes * 60000,
                            );
                            // Adjust for timezone offset to keep local time correct in input
                            const offset = date.getTimezoneOffset() * 60000;
                            const localDate = new Date(date.getTime() - offset);
                            const formatted = localDate
                              .toISOString()
                              .slice(0, 16);

                            setMaintenanceUntil(formatted);
                            // Optional: Auto-update on click? User might prefer manual update.
                            // keeping it manual for now to review date first.
                          }}
                          className="px-2 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                        >
                          +{option.label}
                        </button>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500 italic">
                      Set when maintenance is expected to end. Leaves empty to
                      hide the timer.
                    </p>
                  </div> */}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Exempt Emails
                    </label>
                    <div className="flex gap-2 mb-4">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="form-input dark:text-white"
                        placeholder="Add email to exempt list..."
                        onKeyPress={(e) =>
                          e.key === 'Enter' && handleAddEmail()
                        }
                      />
                      <button
                        onClick={handleAddEmail}
                        disabled={loading || !newEmail}
                        className="btn-primary"
                      >
                        Add Email
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {exemptEmails.length > 0 ? (
                        exemptEmails.map((email) => (
                          <span
                            key={email}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium border border-primary-200 dark:border-primary-800"
                          >
                            {email}
                            <button
                              onClick={() => handleRemoveEmail(email)}
                              className="hover:text-primary-900 dark:hover:text-primary-100 transition-colors"
                            >
                              ×
                            </button>
                          </span>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400 italic">
                          No exempt emails added.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
